import React, { useState } from "react";
import {
  Shield,
  Eye,
  Lock,
  FileText,
  UserCheck,
  AlertCircle,
  Mail,
  Phone,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import SEO from "../Components/SEO";

const PrivacyPolicyPage = () => {
  const [expanded, setExpanded] = useState("introduction");

  const sections = [
    {
      id: "introduction",
      title: "1. Introduction",
      icon: Shield,
      content: (
        <div>
          <p>
            Trademin Ltd. ("we", "us", "our") is committed to protecting your
            privacy and personal information in accordance with the Privacy Act
            2020 and the 13 Information Privacy Principles (IPPs) set out in
            that Act.
          </p>
          <p style={{ marginTop: "1rem" }}>
            This Privacy Policy explains how we collect, use, store, disclose,
            and protect your personal information when you use our services,
            visit our website, or interact with us in any way.
          </p>
          <p style={{ marginTop: "1rem", color: "#1a1a1a", fontWeight: 400 }}>
            Last Updated:{" "}
            {new Date().toLocaleDateString("en-NZ", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </p>
        </div>
      ),
    },
    {
      id: "information-we-collect",
      title: "2. Personal Information We Collect",
      icon: FileText,
      content: (
        <div>
          <p>
            We may collect the following types of personal information from you:
          </p>
          {[
            {
              heading: "Quote Request Information",
              items: [
                "First and last name",
                "Email address",
                "Phone number",
                "Description of the work or service required",
              ],
            },
            {
              heading: "Callback Request Information",
              items: ["First and last name", "Cellphone number"],
            },
            {
              heading: "Service-Related Information",
              items: [
                "Project details and requirements",
                "Service history and correspondence",
                "Quote and invoice information",
              ],
            },
            {
              heading: "Technical Information",
              items: [
                "IP address",
                "Browser type and version",
                "Device information",
                "Website usage data and cookies",
                "Pages visited and time spent on our website",
              ],
            },
          ].map((group, i) => (
            <div key={i} className="pp-info-block">
              <div className="pp-info-heading">{group.heading}</div>
              <ul className="pp-list">
                {group.items.map((item, j) => (
                  <li key={j}>
                    <span className="pp-list-dot" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ),
    },
    {
      id: "how-we-collect",
      title: "3. How We Collect Personal Information",
      icon: UserCheck,
      content: (
        <div>
          <p>We collect personal information directly from you when you:</p>
          <ul className="pp-list" style={{ marginTop: "1rem" }}>
            {[
              "Submit a quote request through our website, providing your name, email, phone number, and service description",
              "Submit a callback request through our website, providing your name and cellphone number",
              "Contact us directly via phone or email",
              "Engage our services for electrical or mechanical work",
              "Visit our website (through cookies and analytics)",
            ].map((item, i) => (
              <li key={i}>
                <span className="pp-list-dot" />
                {item}
              </li>
            ))}
          </ul>
          <p style={{ marginTop: "1rem" }}>
            We may also collect information from third parties such as
            referrals, business partners, or publicly available sources where
            necessary and lawful to do so.
          </p>
        </div>
      ),
    },
    {
      id: "how-we-use",
      title: "4. How We Use Your Personal Information",
      icon: Eye,
      content: (
        <div>
          <p>
            We collect and use your personal information for the following
            purposes, in accordance with the Information Privacy Principles:
          </p>
          {[
            {
              heading: "Service Delivery",
              items: [
                "Preparing and providing quotes for electrical and mechanical services",
                "Calling you back in response to a callback request",
                "Scheduling appointments and site visits",
                "Communicating about your project",
                "Maintaining service records",
              ],
            },
            {
              heading: "Business Operations",
              items: [
                "Managing customer relationships",
                "Maintaining warranty and safety records",
                "Improving our services and website",
                "Conducting business analysis and planning",
              ],
            },
            {
              heading: "Legal and Compliance",
              items: [
                "Complying with electrical and safety regulations",
                "Meeting tax and accounting obligations",
                "Responding to legal requests or disputes",
                "Maintaining compliance certificates and records",
              ],
            },
          ].map((group, i) => (
            <div key={i} className="pp-bordered-block">
              <div className="pp-bordered-heading">{group.heading}</div>
              <ul className="pp-list">
                {group.items.map((item, j) => (
                  <li key={j}>
                    <span className="pp-list-dot" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <p style={{ marginTop: "1.5rem", color: "#1a1a1a", fontWeight: 400 }}>
            We will not use your personal information for purposes other than
            those for which it was collected, unless you have consented or we
            are required or permitted by law to do so.
          </p>
        </div>
      ),
    },
    {
      id: "disclosure",
      title: "5. Disclosure of Personal Information",
      icon: Lock,
      content: (
        <div>
          <p>
            We may disclose your personal information to third parties in the
            following circumstances:
          </p>
          {[
            {
              heading: "Service Providers",
              text: "We may share information with contractors, suppliers, or service providers who assist us in delivering our services (e.g., cloud storage providers, accounting software). These parties are required to protect your information and use it only for the purposes we specify.",
            },
            {
              heading: "Legal Requirements",
              text: "We may disclose information if required by law, court order, or regulatory authority, or to protect our legal rights, safety, or property.",
            },
            {
              heading: "Business Transfers",
              text: "In the event of a merger, acquisition, or sale of assets, your information may be transferred to the new owner, subject to this Privacy Policy.",
            },
            {
              heading: "Consent",
              text: "We may disclose your information to other parties where you have provided explicit consent.",
            },
          ].map((item, i) => (
            <div key={i} className="pp-info-block">
              <div className="pp-info-heading">{item.heading}</div>
              <p>{item.text}</p>
            </div>
          ))}
          <p style={{ marginTop: "1rem", color: "#1a1a1a", fontWeight: 400 }}>
            We do not sell, rent, or trade your personal information to third
            parties for marketing purposes.
          </p>
        </div>
      ),
    },
    {
      id: "storage-security",
      title: "6. Storage and Security of Personal Information",
      icon: Shield,
      content: (
        <div>
          <p>
            We take reasonable steps to protect your personal information from
            misuse, interference, loss, unauthorised access, modification, or
            disclosure.
          </p>
          <div className="pp-highlight-block" style={{ marginTop: "1.25rem" }}>
            <div className="pp-info-heading">Security Measures</div>
            <ul className="pp-list">
              {[
                "Physical security of premises and paper records",
                "Secure password-protected systems and databases",
                "Encryption of sensitive data transmission",
                "Regular security audits and updates",
                "Access controls limiting staff access to personal information",
                "Staff training on privacy and data protection",
              ].map((item, i) => (
                <li key={i}>
                  <span className="pp-list-dot" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="pp-info-block">
            <div className="pp-info-heading">Data Storage</div>
            <p>
              Personal information is stored in New Zealand and may be stored on
              secure cloud servers located in New Zealand or overseas. Where
              information is stored overseas, we ensure adequate protections are
              in place in accordance with IPP 12.
            </p>
          </div>
          <div className="pp-info-block">
            <div className="pp-info-heading">Retention</div>
            <p>
              We retain your personal information for as long as necessary to
              fulfill the purposes for which it was collected, comply with legal
              obligations, and resolve disputes. Electrical compliance
              certificates and safety records are retained in accordance with
              regulatory requirements (typically 7+ years).
            </p>
          </div>
        </div>
      ),
    },
    {
      id: "your-rights",
      title: "7. Your Rights Under the Privacy Act 2020",
      icon: UserCheck,
      content: (
        <div>
          <p>
            Under the Privacy Act 2020, you have the following rights regarding
            your personal information:
          </p>
          <div className="pp-rights-grid">
            {[
              {
                title: "Right to Access",
                text: "You can request access to the personal information we hold about you (IPP 6).",
              },
              {
                title: "Right to Correction",
                text: "You can request correction of any inaccurate, out-of-date, incomplete, or misleading information (IPP 7).",
              },
              {
                title: "Right to Deletion",
                text: "You can request deletion of your personal information, subject to legal and regulatory retention requirements.",
              },
              {
                title: "Right to Object",
                text: "You can object to or opt-out of any communications from us at any time.",
              },
            ].map((right, i) => (
              <div key={i} className="pp-right-card">
                <div className="pp-right-title">{right.title}</div>
                <p>{right.text}</p>
              </div>
            ))}
          </div>
          <div className="pp-info-block" style={{ marginTop: "1.5rem" }}>
            <div className="pp-info-heading">How to Exercise Your Rights</div>
            <p>
              To exercise any of these rights, please contact us using the
              details in Section 10. We will respond to your request within 20
              working days, as required by the Privacy Act 2020. We may need to
              verify your identity before processing your request.
            </p>
          </div>
        </div>
      ),
    },
    {
      id: "cookies",
      title: "8. Cookies and Website Analytics",
      icon: Eye,
      content: (
        <div>
          <p>
            Our website uses cookies and similar tracking technologies to
            improve your browsing experience and analyze website traffic.
          </p>
          <div className="pp-info-block">
            <div className="pp-info-heading">What are Cookies?</div>
            <p>
              Cookies are small text files stored on your device when you visit
              our website. They help us remember your preferences and understand
              how you use our site.
            </p>
          </div>
          <div className="pp-info-block">
            <div className="pp-info-heading">Types of Cookies We Use</div>
            <ul className="pp-list">
              {[
                "Essential Cookies: Required for website functionality",
                "Analytics Cookies: Help us understand website usage (e.g., Google Analytics)",
                "Preference Cookies: Remember your settings and preferences",
              ].map((item, i) => (
                <li key={i}>
                  <span className="pp-list-dot" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="pp-info-block">
            <div className="pp-info-heading">Managing Cookies</div>
            <p>
              You can control or delete cookies through your browser settings.
              Please note that disabling cookies may affect website
              functionality.
            </p>
          </div>
        </div>
      ),
    },
    {
      id: "third-party-links",
      title: "9. Third-Party Websites and Services",
      icon: AlertCircle,
      content: (
        <div>
          <p>
            Our website may contain links to third-party websites or services.
            This Privacy Policy does not apply to those external sites. We are
            not responsible for the privacy practices or content of third-party
            websites.
          </p>
          <div className="pp-info-block">
            <div className="pp-info-heading">Third-Party Service Providers</div>
            <p>
              We use reputable third-party service providers (e.g., cloud
              services, analytics tools) who have their own privacy policies. We
              ensure these providers comply with New Zealand privacy laws and
              adequately protect your information.
            </p>
          </div>
        </div>
      ),
    },
    {
      id: "contact",
      title: "10. Contact Us",
      icon: Phone,
      content: (
        <div>
          <p>
            If you have any questions, concerns, or requests regarding this
            Privacy Policy, please contact us:
          </p>
          <div className="pp-contact-block">
            <div
              className="pp-info-heading"
              style={{ marginBottom: "1.25rem" }}
            >
              Trademin Ltd.
            </div>
            {[
              {
                icon: Mail,
                label: "Email",
                value: "Franco.stander@trademin.co.nz",
                href: "mailto:Franco.stander@trademin.co.nz",
              },
              {
                icon: Phone,
                label: "Phone",
                value: "+64 22 106 2796",
                href: "tel:+6422106796",
              },
              {
                icon: FileText,
                label: "Address",
                value: "Ashburton, Canterbury, New Zealand",
                href: null,
              },
            ].map((item, i) => (
              <div key={i} className="pp-contact-row">
                <item.icon
                  size={14}
                  strokeWidth={1.5}
                  style={{ color: "#E78730", flexShrink: 0, marginTop: 2 }}
                />
                <div>
                  <div className="pp-contact-label">{item.label}</div>
                  {item.href ? (
                    <a
                      href={item.href}
                      className="pp-contact-value pp-contact-link"
                    >
                      {item.value}
                    </a>
                  ) : (
                    <div className="pp-contact-value">{item.value}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="pp-info-block" style={{ marginTop: "1.5rem" }}>
            <div className="pp-info-heading">Privacy Commissioner</div>
            <p>
              If you are not satisfied with our response, you have the right to
              complain to the Office of the Privacy Commissioner:
            </p>
            <div
              style={{
                marginTop: "0.75rem",
                display: "flex",
                flexDirection: "column",
                gap: "0.3rem",
                fontSize: "0.85rem",
                color: "#666",
              }}
            >
              <span>
                Website:{" "}
                <a
                  href="https://www.privacy.org.nz"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#E78730" }}
                >
                  www.privacy.org.nz
                </a>
              </span>
              <span>Phone: 0800 803 909</span>
              <span>Email: enquiries@privacy.org.nz</span>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "changes",
      title: "11. Changes to This Privacy Policy",
      icon: AlertCircle,
      content: (
        <div>
          <p>
            We may update this Privacy Policy from time to time to reflect
            changes in our practices, technology, legal requirements, or
            business operations.
          </p>
          <div className="pp-highlight-block" style={{ marginTop: "1.25rem" }}>
            <p>When we make significant changes, we will:</p>
            <ul className="pp-list" style={{ marginTop: "0.75rem" }}>
              {[
                'Update the "Last Updated" date at the top of this policy',
                "Notify you via email (if we have your email address)",
                "Display a prominent notice on our website",
              ].map((item, i) => (
                <li key={i}>
                  <span className="pp-list-dot" />
                  {item}
                </li>
              ))}
            </ul>
            <p style={{ marginTop: "0.75rem" }}>
              Your continued use of our services after changes are posted
              constitutes acceptance of the updated policy.
            </p>
          </div>
        </div>
      ),
    },
  ];

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500&display=swap');

    .pp-root { font-family: 'DM Sans', sans-serif; background: #fafaf8; }

    /* Hero */
    .pp-hero { background: #111; border-bottom: 1px solid rgba(255,255,255,0.06); padding: 10rem 7vw 7rem; position: relative; overflow: hidden; }
    .pp-hero-watermark { position: absolute; top: 50%; right: 5vw; transform: translateY(-50%); font-family: 'DM Serif Display', serif; font-size: clamp(6rem, 18vw, 16rem); color: rgba(255,255,255,0.025); line-height: 1; user-select: none; pointer-events: none; white-space: nowrap; }
    .pp-hero-glow { position: absolute; bottom: -30%; left: -10%; width: 60%; height: 80%; background: radial-gradient(circle, rgba(231,135,48,0.06) 0%, transparent 70%); pointer-events: none; }
    .pp-hero-content { position: relative; z-index: 1; max-width: 650px; }
    .pp-eyebrow { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 2rem; }
    .pp-eyebrow-line { width: 28px; height: 1px; background: #E78730; flex-shrink: 0; }
    .pp-eyebrow-text { font-size: 0.68rem; letter-spacing: 0.26em; text-transform: uppercase; color: #E78730; font-weight: 500; }
    .pp-hero h1 { font-family: 'DM Serif Display', serif; font-size: clamp(3rem, 7vw, 6rem); line-height: 0.95; color: #fff; margin-bottom: 2rem; }
    .pp-hero h1 em { color: #E78730; font-style: italic; }
    .pp-hero-desc { font-size: 0.95rem; color: rgba(255,255,255,0.38); line-height: 1.8; font-weight: 300; max-width: 52ch; }

    /* Body layout */
    .pp-body { display: grid; grid-template-columns: 260px 1fr; min-height: 80vh; }

    /* Sidebar nav */
    .pp-nav { border-right: 1px solid #e8e8e4; padding: 4rem 0; position: sticky; top: 0; height: 100vh; overflow-y: auto; background: #fafaf8; }
    .pp-nav-item { display: flex; align-items: center; gap: 0.75rem; padding: 0.85rem 2rem; cursor: pointer; font-size: 0.75rem; color: #aaa; font-weight: 400; border-left: 2px solid transparent; transition: color 0.2s, border-color 0.2s, background 0.2s; text-decoration: none; }
    .pp-nav-item:hover { color: #1a1a1a; background: #f4f1ec; }
    .pp-nav-item.active { color: #E78730; border-left-color: #E78730; background: rgba(231,135,48,0.04); }
    .pp-nav-icon { flex-shrink: 0; }

    /* Content */
    .pp-content { padding: 5rem 6vw; }

    /* Accordion sections */
    .pp-section { border-bottom: 1px solid #e8e8e4; }
    .pp-section:first-child { border-top: 1px solid #e8e8e4; }

    .pp-section-trigger { display: flex; align-items: center; justify-content: space-between; padding: 2rem 0; cursor: pointer; gap: 1.5rem; transition: background 0.2s; }
    .pp-section-trigger:hover .pp-section-title { color: #E78730; }

    .pp-section-trigger-left { display: flex; align-items: center; gap: 1.25rem; }
    .pp-section-icon { color: #E78730; flex-shrink: 0; }
    .pp-section-title { font-family: 'DM Serif Display', serif; font-size: 1.25rem; color: #1a1a1a; transition: color 0.2s; }
    .pp-section-chevron { color: #ccc; flex-shrink: 0; transition: color 0.2s; }
    .pp-section-trigger:hover .pp-section-chevron { color: #E78730; }

    .pp-section-body { padding: 0 0 2.5rem 3rem; font-size: 0.875rem; color: #666; line-height: 1.85; font-weight: 300; }

    /* Content sub-elements */
    .pp-info-block { background: #f4f1ec; padding: 1.25rem 1.5rem; margin-top: 1.25rem; }
    .pp-highlight-block { background: rgba(231,135,48,0.06); border-left: 2px solid #E78730; padding: 1.25rem 1.5rem; }
    .pp-info-heading { font-size: 0.65rem; letter-spacing: 0.2em; text-transform: uppercase; color: #E78730; font-weight: 500; margin-bottom: 0.75rem; }
    .pp-bordered-block { border-left: 2px solid #e8e8e4; padding-left: 1.25rem; margin-top: 1.25rem; }
    .pp-bordered-heading { font-size: 0.75rem; font-weight: 500; color: #1a1a1a; margin-bottom: 0.5rem; letter-spacing: 0.04em; }

    .pp-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 0.4rem; }
    .pp-list li { display: flex; align-items: flex-start; gap: 0.75rem; font-size: 0.85rem; color: #666; font-weight: 300; line-height: 1.5; }
    .pp-list-dot { width: 4px; height: 4px; background: #E78730; border-radius: 50%; flex-shrink: 0; margin-top: 7px; }

    .pp-rights-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-top: 1.25rem; }
    .pp-right-card { border: 1px solid #e8e8e4; padding: 1.25rem 1.5rem; background: #fff; }
    .pp-right-title { font-size: 0.7rem; letter-spacing: 0.14em; text-transform: uppercase; color: #E78730; font-weight: 500; margin-bottom: 0.6rem; }

    .pp-contact-block { background: #f4f1ec; padding: 1.75rem 2rem; margin-top: 1.25rem; }
    .pp-contact-row { display: flex; align-items: flex-start; gap: 0.85rem; margin-bottom: 1rem; }
    .pp-contact-row:last-child { margin-bottom: 0; }
    .pp-contact-label { font-size: 0.62rem; letter-spacing: 0.18em; text-transform: uppercase; color: #bbb; font-weight: 500; margin-bottom: 0.2rem; }
    .pp-contact-value { font-size: 0.875rem; color: #1a1a1a; font-weight: 300; }
    .pp-contact-link { text-decoration: none; transition: color 0.2s; }
    .pp-contact-link:hover { color: #E78730; }

    /* Footer compliance banner */
    .pp-compliance { background: #E78730; padding: 3rem 6vw; }
    .pp-compliance-label { font-size: 0.62rem; letter-spacing: 0.22em; text-transform: uppercase; color: rgba(255,255,255,0.6); font-weight: 500; margin-bottom: 0.75rem; }
    .pp-compliance h3 { font-family: 'DM Serif Display', serif; font-size: 1.5rem; color: #fff; margin-bottom: 0.75rem; }
    .pp-compliance p { font-size: 0.88rem; color: rgba(255,255,255,0.75); line-height: 1.75; font-weight: 300; max-width: 80ch; }

    @media (max-width: 900px) {
      .pp-body { grid-template-columns: 1fr; }
      .pp-nav { display: none; }
      .pp-content { padding: 3rem 7vw; }
      .pp-rights-grid { grid-template-columns: 1fr; }
      .pp-section-body { padding: 0 0 2rem 0; }
    }
  `;

  return (
    <>
      <SEO
        title="Privacy Policy | Trademin Ltd"
        description="Read Trademin Ltd's privacy policy to understand how we collect, use, and protect your personal information."
        canonical="https://www.trademin.co.nz/privacy-policy"
        ogTitle="Privacy Policy | Trademin Ltd"
        ogDescription="Trademin Ltd's privacy policy and data protection information."
      />
      <style>{styles}</style>

      <div className="pp-root">
        {/* Hero */}
        <section className="pp-hero">
          <div className="pp-hero-glow" />
          <div className="pp-hero-watermark">PRIVACY</div>
          <div className="pp-hero-content">
            <div className="pp-eyebrow">
              <span className="pp-eyebrow-line" />
              <span className="pp-eyebrow-text">Privacy & Data Protection</span>
            </div>
            <h1>
              Privacy <em>Policy</em>
            </h1>
            <p className="pp-hero-desc">
              Your privacy matters to us. This policy explains how we collect,
              use, and protect your personal information in compliance with the
              Privacy Act 2020.
            </p>
          </div>
        </section>

        {/* Body: sidebar + content */}
        <div className="pp-body">
          <nav className="pp-nav">
            {sections.map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.id}
                  className={`pp-nav-item${expanded === s.id ? " active" : ""}`}
                  onClick={() => {
                    setExpanded(s.id);
                    document
                      .getElementById(s.id)
                      ?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                >
                  <Icon size={13} strokeWidth={1.5} className="pp-nav-icon" />
                  {s.title}
                </div>
              );
            })}
          </nav>

          {/* Accordion content */}
          <div className="pp-content">
            {sections.map((section) => (
              <div
                key={section.id}
                id={section.id}
                className="pp-section"
                style={{ scrollMarginTop: "2rem" }}
              >
                <div
                  className="pp-section-trigger"
                  onClick={() =>
                    setExpanded(expanded === section.id ? null : section.id)
                  }
                >
                  <div className="pp-section-trigger-left">
                    <section.icon
                      size={16}
                      strokeWidth={1.25}
                      className="pp-section-icon"
                    />
                    <h2 className="pp-section-title">{section.title}</h2>
                  </div>
                  <span className="pp-section-chevron">
                    {expanded === section.id ? (
                      <ChevronUp size={16} strokeWidth={1.5} />
                    ) : (
                      <ChevronDown size={16} strokeWidth={1.5} />
                    )}
                  </span>
                </div>
                {expanded === section.id && (
                  <div className="pp-section-body">{section.content}</div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Compliance banner */}
        <div className="pp-compliance">
          <div className="pp-compliance-label">New Zealand</div>
          <h3>Privacy Act 2020 Compliance</h3>
          <p>
            This Privacy Policy has been prepared to comply with the Privacy Act
            2020 and the 13 Information Privacy Principles. We are committed to
            protecting your privacy and handling your personal information
            responsibly and transparently.
          </p>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicyPage;
